import { getSnapshot, getRoot, types as t, IAnyModelType } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import { reaction, observable, action, runInAction, flow, makeObservable, IObservableArray } from 'mobx'
import fetch from '../../utils/fetch'
import { v4 as uuid } from 'uuid'
import Mapping from './mapping'
import Field from './field'
import DataTransformer from './filter'
import get from 'lodash/get'
import toRecords from './record/record'
import toJS from '../../utils/tojs'
import ref from './reference'
import upsertDataToArray from '../../utils/upsertDataToArray'
import { autorun } from 'mobx'
import DataSource from './datasource'

export class DataStoreProvider {
  data = [] as IObservableArray
  isLoading = true
  dataFetched = false
  config

  constructor (config) {
    makeObservable(this, {
      data: observable.shallow,
      isLoading: observable,
      remove: action
    })

    this.config = config
    autorun(() => {
      this.config.dataTransformer?.qString
      this.dataFetched = false
    })
  }

  /**
    * @param {(Record|Record[])} items
    */
  remove = async _items => {
    _items = _items && (Array.isArray(_items) ? _items : [_items])
    
    if (!_items || !_items.length) return;
    const items = toJS(_items)

    await fetch(
      `/api/datasource/${this.config.dbKey}/remove/${this.config.tableName}`,
      {
        method: 'DELETE',
        body: JSON.stringify(items.map(item => ({ _id: item._id }))),
        headers: { 'Content-Type': 'application/json' }
      }
    )
    runInAction(() => {
      for (const item of items) {
        this.data.remove(this.data.find(o => o._id === item._id))
      }
    })
  }
  
  /*
     PARAMS: _item, original
     OR
     PARAMS: _item: [ item, ... ]
     OR
     PARAMS: _item: [ [ item, original ], ... ]
   */
  upsert = flow(function * (this: DataStoreProvider, _item, _original = {}) {
    // handle overloaded params
    const snapshotsToUpdate = []
    const snapshotsToInsert = []
    const alreadyExistingItems = new Set()

    const rows = upsertDataToArray(_item, _original)
    // Copy this.data Store
    const recordsToUpdate = [...this.data]
    for (let { item, original } of rows) {
      const isNew = !((item && item._id) || (original && original._id))
      const id = (item && item._id ? item._id : (original && original._id ? original._id : uuid()))
      item = item && item.$ && item.$.snapshot ? item.$.snapshot || {} : item || {}
      const currentItemIndex = id && this.data.findIndex(o => o._id === id)
      let currentItem = currentItemIndex >= 0 && this.data[currentItemIndex]

      const isUpdating = !!currentItem
      if (isUpdating) alreadyExistingItems.add(id)

      if (currentItem) {
        runInAction(() => {
          // Modify the current item
          recordsToUpdate[currentItemIndex] = new this.config.Record({ ...currentItem.$.snapshot, ...item })
          // this.data[currentItemIndex] = new this.config.Record({ ...currentItem.$.snapshot, ...item })
        })
        currentItem = recordsToUpdate[currentItemIndex]
      } else {
        item._id = id
        currentItem = new this.config.Record(item)
      }

      const snapshot = currentItem && currentItem.$ ? currentItem.$.snapshot : currentItem
      if (isNew) snapshotsToInsert.push(snapshot)
      else snapshotsToUpdate.push(snapshot)
    }
    // Apply changes to this.data
    this.data = recordsToUpdate as IObservableArray

    const upsertRes = []
    for (const e of [{ snapshots: snapshotsToUpdate, op: 'update' }, { snapshots: snapshotsToInsert, op: 'insert' }]) {
      const upsertPromise = e.snapshots.length && fetch(
        `/api/datasource/${this.config.dbKey}/upsert/${this.config.tableName}/?op=${e.op}`,
        { method: 'POST', body: JSON.stringify(e.snapshots), headers: { 'Content-Type': 'application/json' } }
      )
      const lambdaUpsertRes = yield upsertPromise
      const results = yield lambdaUpsertRes ? lambdaUpsertRes.json() : []
      upsertRes.push(...results)
    }

    const res = []
    for (const upsertedItem of upsertRes) {
      const upsertedItemRecord = new this.config.Record(upsertedItem)
      if (alreadyExistingItems.has(upsertedItemRecord._id)) {
        const index = this.data.findIndex(dataItem => dataItem._id === upsertedItemRecord._id)
        this.data[index] = upsertedItemRecord
      } else {
        res.push(upsertedItemRecord)
      }
    }
    this.data.push(...res)

    return res
  }).bind(this)

  search = (searchValue, fields) => fetch(
    `/api/datasource/${this.config.dbKey}/query/${this.config.tableName}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        search: { 
          text: searchValue, 
          fields: fields && Array.isArray(fields) ? fields.map(f => f.fieldPath || f) : null
        } 
      })
    })
    .then(r => r.json())

  getRecordByKey = (key, id) => {
    return this.data && this.data.find(d => d[key] === id)
  }

  lookup = async (searchValue = '', refData) => {
    if (!this.dataFetched) await this.query(null, false)
    if (!refData) return []
    const { label, value } = refData
    const data = this.data
    const matches =
      (data &&
       data.filter(item => {
         let found
         if (typeof item[label] === 'string') {
           found =
             item[label].toLowerCase().indexOf(searchValue.toLowerCase()) !==
               -1
         }
         if (!found && typeof item[value] === 'string') {
           found = item[value] === searchValue
         }
         return found
       })) ||
      []
    return matches
  }

  serverQuery = async (opts = { where: void 0 }) => {
    if (this.config.dataTransformer?.where) opts.where = [
      await this.config.dataTransformer.where.toSFasync(),
      ...Array.prototype.concat.call(opts.where || [])
    ]
    return this._query(opts)
  }
  
  _query = async ({ count, distinct, where, groupBy, order, limit, offset, search }: Record<string, string>) => {
    let data = await fetch(`/api/datasource/${this.config.dbKey}/query/${this.config.tableName}`, {
      method: 'POST',
      body: JSON.stringify({ count, distinct, where, groupBy, order, limit, offset, search }),
      headers: { 'Content-Type': 'application/json' }
    })

    if (!data.ok) {
      throw new Error('Failed Fetching')
    }

    data = await data.json()

    return Array.isArray(data) && !count ? toRecords(this.config.mapping.children[0], data) : data
  }

  query = flow(function * (this: DataStoreProvider) {
    this.isLoading = true
    let where
    if (this.config.dataTransformer?.where) where = yield this.config.dataTransformer.where.toSFasync()

    try {
      this.data = yield this._query({ where })
    } catch (e) {
      return this.data
    }

    this.dataFetched = true
    this.isLoading = false
    return this.data
  }).bind(this)
}
const defaultFields = [
  ['_id', 'ID', 'id'],
  ['_created_at', 'DATETIME', 'Created Date'],
  ['_created_by', 'STRING', 'Created By'],
  ['_updated_at', 'DATETIME', 'Last Modified Date'],
  ['_updated_by', 'STRING', 'Last Modified By']
]

const DataStore = t
  .model('DataStore', {
    type: t.optional(t.string, 'DataStore'),
    _tableName: t.maybeNull(t.string),
    _fields: t.maybeNull(t.array(Field)),
    dataTransformer: t.late(() => t.maybeNull(DataTransformer)),
    visibilityWhereClause: t.maybeNull(t.string),
    parentDataSource: ref(t.late((): IAnyModelType => (DataSource as any)), 'dataSources')
  })
  .views(self => ({
    get icon () {
      return 'data-connection'
    },
    get Record () {
      return (self as any).mapping.children[0].ItemClass
    }
  }))
  .actions(self => ({
    setFields (f) {
      self._fields = f
    },
    setTableName (f) {
      self._tableName = f
    }
  }))
  .views(self => {
    let mapping
    return {
      get provider () {
        return DataStoreProvider
      },
      get fields () {
        if (self.parentDataSource) return self.parentDataSource.fields
        return self._fields
      },
      set fields (f) {
        self.setFields(f)
      },
      get tableName () {
        if (self.parentDataSource) return self.parentDataSource.tableName
        return self._tableName
      },
      set tableName (f) {
        self.setTableName(f)
      },
      getFields (dsId: string) {
        if (!dsId) {
          return [...defaultFields.map(([fieldPath, type, label]) => ({ fieldPath, type, label })), ...(self as any).fields]
        }
        const ds = (getRoot(self) as any).dataSources.get(dsId)
        return (ds && ds.getFields && ds.getFields()) || []
      },
      get referenceFields () {
        return (self as any).mapping.children[0].children.filter(child => child.type === 'reference')
      },
      get mapping () {
        if (mapping) return mapping
        const selfId = (self as any).id as string
        const children = (getSnapshot((self as any).fields) as any).map(f => ({
          type: f.type && f.type.toLowerCase(),
          key: f.fieldPath,
          bindingType: 'data',
          dataSource: selfId,
          value: f
        }))
        children.unshift(...defaultFields.map(([key, type]) => ({
          type, key, bindingType: 'data', dataSource: selfId, value: { fieldPath: key }
        })))

        const m = {
          dataSource: selfId,
          bindingType: 'data',
          type: 'list',
          children: [
            {
              type: 'Record',
              key: '0',
              recordType: selfId,
              children
            }
          ]
        }

        mapping = Mapping.create(m, {
          getOrLoadObject: (getRoot(self) as any).getOrLoadObject,
          parentDataSource: self,
          ro: true
        })
        return mapping
      },
      get store () {
        return getRoot(self)
      }
    }
  })
  .views(self => ({
    get isValid () {
      return self.tableName?.length
    },
    async fetchChildNodes ({ meta = void 0 }) {
      let res = []
      const refData = Array.prototype.concat(meta && meta.referenceData).filter(x => x)
      for (const rd of refData) {
        const ds = (getRoot(self) as any)
          .dataSources.get(rd.dataSourceId)
        if (ds) {
          let nodes = toJS(ds.labels().childNodes[1].childNodes)
          if (nodes) {
            nodes = nodes.map(n => ({ ...n, secondaryLabel: n.secondaryLabel + ' ' + ds.name }))
            res = res.concat(nodes)
          }
        }
      }
      return res
    },
    labels () {
      let childNodes = defaultFields.map(([key, secondaryLabel, label]) => ({ meta: { type: secondaryLabel }, key, fieldType: secondaryLabel, label }))
      if (self.mapping && self.mapping.children) {
        childNodes = childNodes.concat(self.fields.map(value => {
          return {
            key: value.fieldPath + '',
            label: value.label + '',
            fieldType: value.type,
            meta: toJS(value),
            asynch: value.type && value.type.indexOf('REFERENCE') !== -1,
            childNodes: value.type && value.type.indexOf('REFERENCE') !== -1 && []
          }
        }))
      }

      return {
        key: (self as any).id as string,
        label: (self as any).name as string,
        childNodes: [
          {
            key: 'methods',
            label: 'Methods',
            childNodes: [
              {
                key: 'remove',
                label: 'remove',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'upsert',
                label: 'upsert',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'query',
                label: 'query',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'serverQuery',
                label: 'serverQuery',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'lookup',
                label: 'lookup',
                secondaryLabel: 'FUNCTION'
              },
              {
                key: 'search',
                label: 'search',
                secondaryLabel: 'FUNCTION'
              }
            ]
          },
          {
            key: 'data',
            label: 'Data',
            childNodes
          },
          {
            key: 'state',
            label: 'State',
            childNodes: [
              {
                key: 'isLoading',
                label: 'isLoading',
                secondaryLabel: 'state'
              }
            ]
          }
        ]
      }
    }
  }))
  .actions(crudActions)
  .actions(self => ({
    afterSave (before, after) {
      if (!before.fields || !after.fields || !self.tableName) return
      const updComputed = []
      const updIndexed = []
      const delIndexed = []
      for (const f of after.fields) {
        const old = before.fields.find(ff => ff.fieldPath === f.fieldPath)
        if (f.computed) {
          if (!old || (old.computed !== f.computed || old.code !== f.code)) {
            updComputed.push(f)
          }
        }
        if (f.indexed) {
          if (!old || f.indexed !== old.indexed) {
            updIndexed.push(f)
          }
        } else if (old && old.indexed) {
          delIndexed.push(f)
        }
      }
      Promise.all([
        updComputed.length && fetch(`/api/tables/${self.tableName}/columns`, {
          method: 'POST',
          body: JSON.stringify(updComputed),
          headers: { 'Content-Type': 'application/json' }
        }),
        updIndexed.length && fetch(`/api/tables/${self.tableName}/index`, {
          method: 'POST',
          body: JSON.stringify(updIndexed),
          headers: { 'Content-Type': 'application/json' }
        }),
        delIndexed.length && fetch(`/api/tables/${self.tableName}/index`, {
          method: 'DELETE',
          body: JSON.stringify(delIndexed),
          headers: { 'Content-Type': 'application/json' }
        })
      ].filter(Boolean))
    },
    addField () {
      if (!self.fields) {
        self.fields = []
      }
      self.fields.push({ type: 'STRING' })
    },
    afterCreate () {
      if (!self.fields) self.fields = []
    }
  }))

export default DataStore
